import React from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import {
    fetchCompletedModuleIds,
    fetchResources,
    markCourseCompleted,
    markModuleCompleted,
} from "../utils/resources";
import { downloadCompletionBadge } from "../utils/downloadCmsAsset";
import { ModuleDto, ResourceDto } from "../types/resources";
import { moduleVideoAnalytics, WORDPRESS_TAB } from "../services/courseEventService";
import VideoLightbox from "./VideoLightbox";
import ExpertModal from "./ExpertModal";
import CompletionModal from "./CompletionModal";
import "../styles/resources.css";
import "../styles/course-cards.css";
import "../styles/resource-detail.css";

type SelectedVideo = {
    id: number;
    title: string;
    vimeoUrl: string;
};

export default function ResourceDetailPage() {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();

    const [resource, setResource] = React.useState<ResourceDto | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [expandedModuleIds, setExpandedModuleIds] = React.useState<Set<number>>(new Set());
    const [selectedVideo, setSelectedVideo] = React.useState<SelectedVideo | null>(null);
    const [isExpertOpen, setExpertOpen] = React.useState(false);
    const [completedModuleIds, setCompletedModuleIds] = React.useState<Set<number>>(new Set());
    const [isCompletionOpen, setCompletionOpen] = React.useState(false);
    const [isDownloadingBadge, setIsDownloadingBadge] = React.useState(false);
    const [downloadBadgeError, setDownloadBadgeError] = React.useState<string | null>(null);

    React.useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                setLoading(true);
                setError(null);
                const all = await fetchResources();
                if (cancelled) return;
                const match = all.find(r => r.slug === slug) ?? null;
                setResource(match);
                if (!match) {
                    setError("Resource not found");
                    return;
                }
                try {
                    const completedIds = await fetchCompletedModuleIds(match.id);
                    if (cancelled) return;
                    setCompletedModuleIds(new Set(completedIds));
                } catch {
                    if (!cancelled) setCompletedModuleIds(new Set());
                }
            } catch (err) {
                if (cancelled) return;
                setError(err instanceof Error ? err.message : "Failed to load resource");
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => { cancelled = true; };
    }, [slug]);

    function toggleModule(id: number) {
        setExpandedModuleIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }

    function handlePlayModule(module: ModuleDto) {
        if (!module.videoUrl) return;
        setSelectedVideo({
            id: module.id,
            title: module.title,
            vimeoUrl: module.videoUrl,
        });
    }

    function handleVideoEnd(moduleId: number) {
        if (!resource) return;

        const next = new Set(completedModuleIds);
        const wasAlreadyComplete = next.has(moduleId);
        next.add(moduleId);
        setCompletedModuleIds(next);

        setSelectedVideo(null);

        if (!wasAlreadyComplete) {
            // Fire-and-forget: server is the source of truth, but we don't block UI on the round-trip.
            // The next page load will reconcile from the server response.
            markModuleCompleted(resource.id, moduleId).catch(() => {
                // Silently ignore; user will retry on next video end and the next load will correct state.
            });
        }

        const allModuleIds = resource.modules.map(m => m.id);
        const allCompleted =
            allModuleIds.length > 0 && allModuleIds.every(id => next.has(id));

        if (allCompleted) {
            // Persist course-level completion (idempotent). Reflected on the resources
            // listing on next load.
            markCourseCompleted(resource.id).catch(() => {
                // Silently ignore; will reconcile on next reload.
            });
            window.setTimeout(() => setCompletionOpen(true), 300);
        }
    }

    if (loading) {
        return (
            <div className="resources-page">
                <section className="courses-section">
                    <div className="loading-spinner">Loading...</div>
                </section>
            </div>
        );
    }

    if (error || !resource) {
        return (
            <div className="resources-page">
                <section className="courses-section">
                    <button
                        type="button"
                        className="resource-detail-back"
                        onClick={() => navigate("/resources")}
                    >
                        ← Back
                    </button>
                    <div className="resource-detail-error" style={{ color: "#991b1b" }}>
                        {error ?? "Resource not found"}
                    </div>
                </section>
            </div>
        );
    }

    const moduleCount = resource.modules?.length ?? 0;
    const sanitizedDescription = DOMPurify.sanitize(resource.excerpt || "");
    const moduleIds = resource.modules.map(m => m.id);
    const allModulesComplete =
        moduleIds.length > 0 && moduleIds.every(id => completedModuleIds.has(id));
    const showDownloadBadge =
        allModulesComplete
        && Boolean(
            resource.courseCompletionModalDownloadUrl || resource.courseCompletionModalBadgeUrl
        );

    const handleDownloadBadge = async () => {
        setDownloadBadgeError(null);
        setIsDownloadingBadge(true);
        try {
            await downloadCompletionBadge(
                resource.courseCompletionModalDownloadUrl,
                resource.courseCompletionModalBadgeUrl
            );
        } catch (error) {
            setDownloadBadgeError(
                error instanceof Error ? error.message : "Unable to download badge. Please try again."
            );
        } finally {
            setIsDownloadingBadge(false);
        }
    };

    return (
        <div className="resources-page resource-detail-page">
            <section className="courses-section">
                <div className="resource-detail-top">
                    <div className="resource-detail-top-main">
                        <button
                            type="button"
                            className="resource-detail-back"
                            onClick={() => navigate("/resources")}
                            aria-label="Back to Resources"
                        >
                            ← Back
                        </button>

                        <nav className="resource-detail-breadcrumb" aria-label="Breadcrumb">
                            <Link to="/resources">Resources</Link>
                            <span className="breadcrumb-separator"> / </span>
                            <span className="breadcrumb-current">{resource.title}</span>
                        </nav>
                    </div>

                    {showDownloadBadge && (
                        <div className="resource-detail-download-badge-wrap">
                            {downloadBadgeError && (
                                <p className="resource-detail-download-error" role="alert">
                                    {downloadBadgeError}
                                </p>
                            )}
                            <button
                                type="button"
                                className="rcard-launch resource-detail-download-badge"
                                onClick={handleDownloadBadge}
                                disabled={isDownloadingBadge}
                            >
                                {isDownloadingBadge
                                    ? "Downloading..."
                                    : resource.courseCompletionModalLinkText || "Download Badge"}
                            </button>
                        </div>
                    )}
                </div>

                <header className="resource-detail-header">
                    <div className="resource-detail-image">
                        {resource.featuredImageUrl ? (
                            <img src={resource.featuredImageUrl} alt={resource.title} />
                        ) : (
                            <div className="resource-detail-image-placeholder" />
                        )}
                    </div>

                    <div className="resource-detail-info">
                        <h1 className="resource-detail-title">{resource.title}</h1>

                        {resource.expert && (
                            <div className="resource-detail-expert">
                                <div className="resource-detail-expert-avatar">
                                    <img
                                        src={resource.expert.expertImageUrl}
                                        alt={resource.expert.title}
                                    />
                                </div>
                                <button
                                    className="resource-detail-expert-name"
                                    onClick={() => setExpertOpen(true)}
                                >
                                    {resource.expert.title}
                                </button>
                            </div>
                        )}

                        {sanitizedDescription && (
                            <div
                                className="resource-detail-description"
                                dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
                            />
                        )}

                        {resource.label && (
                            <div className="resource-detail-label-row">
                                <span className="rcard-label-pill">{resource.label}</span>
                            </div>
                        )}
                    </div>
                </header>

                <section className="resource-detail-contents">
                    <h2 className="resource-detail-contents-title">Course Contents</h2>
                    <div className="resource-detail-contents-meta">
                        <span className="rcard-meta-chip">
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden
                            >
                                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                            </svg>
                            {moduleCount} {moduleCount === 1 ? 'Session' : 'Sessions'}
                        </span>
                        {resource.lengthOfCourse && (
                            <span className="rcard-meta-chip">
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden
                                >
                                    <circle cx="12" cy="12" r="9" />
                                    <polyline points="12 7 12 12 15 14" />
                                </svg>
                                {resource.lengthOfCourse}
                            </span>
                        )}
                    </div>

                    {moduleCount === 0 ? (
                        <div className="resource-detail-empty-modules">
                            No modules available for this course yet.
                        </div>
                    ) : (
                        <ul className="resource-detail-modules">
                            {resource.modules.map((module) => {
                                const expanded = expandedModuleIds.has(module.id);
                                const completed = completedModuleIds.has(module.id);
                                return (
                                    <li
                                        key={module.id}
                                        className={`resource-detail-module ${expanded ? "expanded" : ""} ${completed ? "completed" : ""}`}
                                    >
                                        <button
                                            type="button"
                                            className="resource-detail-module-header"
                                            aria-expanded={expanded}
                                            onClick={() => toggleModule(module.id)}
                                        >
                                            <span
                                                className={`resource-detail-module-chevron ${expanded ? "expanded" : ""}`}
                                                aria-hidden
                                            >
                                                <svg
                                                    width="14"
                                                    height="14"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <polyline points="6 9 12 15 18 9" />
                                                </svg>
                                            </span>
                                            <span
                                                className="resource-detail-module-title"
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(module.title),
                                                }}
                                            />

                                            {completed && (
                                                <span
                                                    className="resource-detail-module-completed"
                                                    data-testid={`module-completed-${module.id}`}
                                                >
                                                    <svg
                                                        width="14"
                                                        height="14"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="3"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        aria-hidden
                                                    >
                                                        <polyline points="20 6 9 17 4 12" />
                                                    </svg>
                                                    Completed
                                                </span>
                                            )}
                                        </button>

                                        {expanded && (
                                            <div className="resource-detail-module-body">
                                                {module.length && (
                                                    <div className="resource-detail-module-meta">
                                                        <span className="rcard-meta-chip">
                                                            <svg
                                                                width="14"
                                                                height="14"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                aria-hidden
                                                            >
                                                                <circle cx="12" cy="12" r="9" />
                                                                <polyline points="12 7 12 12 15 14" />
                                                            </svg>
                                                            {module.length}
                                                        </span>
                                                    </div>
                                                )}
                                                <div className="resource-detail-module-content">
                                                    {module.imageUrl && (
                                                        <button
                                                            type="button"
                                                            className="resource-detail-module-image"
                                                            onClick={() => handlePlayModule(module)}
                                                            disabled={!module.videoUrl}
                                                            aria-label={`Play ${module.title}`}
                                                        >
                                                            <img src={module.imageUrl} alt={module.title} />
                                                            {module.videoUrl && (
                                                                <span className="resource-detail-module-play" aria-hidden>▶</span>
                                                            )}
                                                        </button>
                                                    )}
                                                    {module.descriptionHtml && (
                                                        <div
                                                            className="resource-detail-module-description"
                                                            dangerouslySetInnerHTML={{
                                                                __html: DOMPurify.sanitize(module.descriptionHtml),
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </section>
            </section>

            <VideoLightbox
                open={!!selectedVideo}
                resource={selectedVideo}
                onClose={() => setSelectedVideo(null)}
                onVideoEnd={handleVideoEnd}
                analytics={
                    resource && selectedVideo
                        ? moduleVideoAnalytics(WORDPRESS_TAB.RESOURCES, resource.id, selectedVideo.id)
                        : null
                }
            />

            {isCompletionOpen && (
                <CompletionModal
                    resource={resource}
                    onClose={() => setCompletionOpen(false)}
                />
            )}

            {isExpertOpen && (
                <ExpertModal
                    expert={resource.expert}
                    onClose={() => setExpertOpen(false)}
                />
            )}
        </div>
    );
}
